import { useState, useEffect, useCallback } from 'react';
import { fetchComplaints, updateComplaintStatus, type Complaint } from '@/lib/supabase';

const STATUS_CONFIG = {
  new:         { label: 'New',         bg: 'bg-red-50',    border: 'border-red-300',    text: 'text-red-600',    dot: 'bg-red-500'    },
  in_progress: { label: 'In Progress', bg: 'bg-amber-50',  border: 'border-amber-300',  text: 'text-amber-600',  dot: 'bg-amber-500'  },
  resolved:    { label: 'Resolved',    bg: 'bg-green-50',  border: 'border-green-300',  text: 'text-green-600',  dot: 'bg-green-500'  },
};

const TYPE_ICONS: Record<string, string> = {
  'Order Not Delivered':    'ri-truck-line',
  'Wrong Product Received': 'ri-exchange-line',
  'Product Quality Issue':  'ri-thumb-down-line',
  'Refund/Return Request':  'ri-refund-2-line',
  'Payment Issue':          'ri-bank-card-line',
  'Delivery Delay':         'ri-time-line',
  'Size/Fit Issue':         'ri-ruler-line',
  'Other':                  'ri-question-line',
};

export default function ComplaintsPanel() {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<'all' | Complaint['status']>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await fetchComplaints();
    setComplaints(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id: string, status: Complaint['status']) => {
    setUpdatingId(id);
    await updateComplaintStatus(id, status);
    setComplaints(prev => prev.map(c => c.id === id ? { ...c, status } : c));
    setUpdatingId(null);
  };

  const filtered = filterStatus === 'all' ? complaints : complaints.filter(c => c.status === filterStatus);
  const counts = {
    all: complaints.length,
    new: complaints.filter(c => c.status === 'new').length,
    in_progress: complaints.filter(c => c.status === 'in_progress').length,
    resolved: complaints.filter(c => c.status === 'resolved').length,
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <i className="ri-customer-service-2-line text-red-600 text-lg"></i>
            </div>
            <div>
              <p className="text-gray-900 font-bold text-base">Customer Complaints</p>
              <p className="text-gray-400 text-xs">Register complaint form se aaye submissions</p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
            Refresh
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 flex-wrap">
          {([
            { key: 'all',         label: 'All',         icon: 'ri-list-check'            },
            { key: 'new',         label: 'New',         icon: 'ri-error-warning-line'    },
            { key: 'in_progress', label: 'In Progress', icon: 'ri-loader-4-line'         },
            { key: 'resolved',    label: 'Resolved',    icon: 'ri-checkbox-circle-line'  },
          ] as { key: 'all' | Complaint['status']; label: string; icon: string }[]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilterStatus(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                filterStatus === tab.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${filterStatus === tab.key ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>
                {counts[tab.key]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl p-3 border border-gray-100 text-center">
          <p className="text-2xl font-bold text-red-600">{counts.new}</p>
          <p className="text-gray-400 text-xs mt-0.5">New</p>
        </div>
        <div className="bg-white rounded-2xl p-3 border border-gray-100 text-center">
          <p className="text-2xl font-bold text-amber-600">{counts.in_progress}</p>
          <p className="text-gray-400 text-xs mt-0.5">In Progress</p>
        </div>
        <div className="bg-white rounded-2xl p-3 border border-gray-100 text-center">
          <p className="text-2xl font-bold text-green-600">{counts.resolved}</p>
          <p className="text-gray-400 text-xs mt-0.5">Resolved</p>
        </div>
      </div>

      {/* Complaints List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin"></div>
          <p className="text-gray-500 text-sm">Loading complaints...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 gap-3 bg-white rounded-2xl border border-gray-100">
          <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center">
            <i className="ri-checkbox-circle-line text-3xl text-green-400"></i>
          </div>
          <p className="text-gray-700 font-semibold">Koi complaint nahi!</p>
          <p className="text-gray-400 text-sm">{filterStatus === 'all' ? 'Abhi tak koi complaint nahi aayi' : `Koi ${STATUS_CONFIG[filterStatus as Complaint['status']]?.label} complaint nahi`}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(complaint => {
            const sc = STATUS_CONFIG[complaint.status];
            const isExpanded = expandedId === complaint.id;
            return (
              <div
                key={complaint.id}
                className={`bg-white rounded-2xl border transition-all ${complaint.status === 'new' ? 'border-red-200' : 'border-gray-100'}`}
              >
                {/* Card Header */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : complaint.id)}
                >
                  <div className="flex items-start gap-3">
                    {/* Issue icon */}
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                      <i className={`${TYPE_ICONS[complaint.complaint_type] ?? 'ri-question-line'} text-gray-600 text-base`}></i>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <p className="text-gray-900 font-bold text-sm">{complaint.name}</p>
                        {complaint.status === 'new' && (
                          <span className="px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full animate-pulse">NEW</span>
                        )}
                      </div>
                      <p className="text-gray-500 text-xs flex items-center gap-1">
                        <i className="ri-phone-line"></i>{complaint.phone}
                        {complaint.order_id && (
                          <span className="ml-2 bg-gray-100 px-2 py-0.5 rounded-full font-mono text-[10px]">{complaint.order_id}</span>
                        )}
                      </p>
                      <p className="text-gray-600 text-xs mt-1 font-medium">{complaint.complaint_type}</p>
                    </div>

                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${sc.bg} ${sc.border} ${sc.text} flex items-center gap-1`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                        {sc.label}
                      </span>
                      <p className="text-gray-400 text-[10px]">
                        {new Date(complaint.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>

                  {/* Preview message */}
                  {!isExpanded && (
                    <p className="text-gray-500 text-xs mt-2 ml-13 line-clamp-2 pl-[52px]">{complaint.message}</p>
                  )}
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                    {/* Full message */}
                    <div className="bg-gray-50 rounded-xl p-3 mb-3">
                      <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wide mb-1">Customer Message</p>
                      <p className="text-gray-700 text-sm leading-relaxed">{complaint.message}</p>
                    </div>

                    {/* Customer contact buttons */}
                    <div className="flex gap-2 mb-3">
                      <a
                        href={`tel:${complaint.phone}`}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-orange-50 border border-orange-200 text-orange-600 font-bold text-xs hover:bg-orange-100 transition-all cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-phone-fill"></i>Call Customer
                      </a>
                      <a
                        href={`https://wa.me/${complaint.phone.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-green-50 border border-green-200 text-green-600 font-bold text-xs hover:bg-green-100 transition-all cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-fill"></i>WhatsApp
                      </a>
                    </div>

                    {/* Status Update */}
                    <div>
                      <p className="text-xs font-bold text-gray-600 mb-2">Update Status:</p>
                      <div className="flex gap-2 flex-wrap">
                        {(['new', 'in_progress', 'resolved'] as Complaint['status'][]).map(s => {
                          const cfg = STATUS_CONFIG[s];
                          return (
                            <button
                              key={s}
                              onClick={() => handleStatusChange(complaint.id, s)}
                              disabled={complaint.status === s || updatingId === complaint.id}
                              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap border ${
                                complaint.status === s
                                  ? `${cfg.bg} ${cfg.border} ${cfg.text} cursor-default`
                                  : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-400 disabled:opacity-50'
                              }`}
                            >
                              {updatingId === complaint.id && complaint.status !== s ? (
                                <i className="ri-loader-4-line animate-spin"></i>
                              ) : (
                                <span className={`w-2 h-2 rounded-full ${cfg.dot}`}></span>
                              )}
                              {cfg.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
